import React, { useState, useEffect } from "react"
import { get, post, put, remove } from "../../utils/apiClient"
import toast from "react-hot-toast"
import Pagination from "../common/pagination"
import {
  validateFaxNumberOnlyDigit,
  formatFaxNumber,
  prepareFinalFaxNumber,
} from "../../utils/common"
import { SquarePen, Trash2 } from "lucide-react"

const QueueToFaxNumberAssociation = ({ reloadQueues }) => {
  const [queues, setQueues] = useState([])
  const [showPopup, setShowPopup] = useState(false)
  const [queueToEdit, setQueueToEdit] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)
  const [queueIdToDelete, setQueueIdToDelete] = useState(null)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [searchText, setSearchText] = useState("")

  // Search Filter
  const filteredQueues = queues?.filter((queue) => {
    return Object.entries(queue).some(([key, value]) => {
      // For other fields, apply standard filtering
      if (key === "queueName") {
        return value
          ?.toString()
          .toLowerCase()
          .includes(searchText.toLowerCase())
      }
    })
  })

  const startIdx = (currentPage - 1) * rowsPerPage
  const currentQueues = filteredQueues.slice(startIdx, startIdx + rowsPerPage)
  const totalPages = Math.ceil(filteredQueues.length / rowsPerPage)

  // const currentFaxes = sortedFaxes.slice(startIdx, startIdx + rowsPerPage)
  // const totalPages = Math.ceil(filteredFaxes.length / rowsPerPage)

  useEffect(() => {
    loadQueue()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchQueues = async () => {
    try {
      const data = await get("/admin/queue-fax-number-associations")
      setQueues(data)
    } catch (error) {
      console.error("Failed to fetch queues", error)
    }
  }

  const loadQueue = async () => {
    setLoading(true)
    await fetchQueues()
    setLoading(false)
  }

  const handleAddQueue = async (data) => {
    setActionLoading(true)

    const isEdit = !!data?.associationId

    try {
      if (isEdit) {
        await put(`/admin/queue-fax-number-association`, {
          associationId: data.associationId,
          queueName: data.queueName,
          faxNumber: data.faxNumber,
          // queueOldName: data.queueOldName,
          // queueNewName: data.queueNewName,
        })
      } else {
        await post("/admin/queue-fax-number-association", {
          queueName: data.queueName,
          faxNumber: data.faxNumber,
        })
      }

      fetchQueues()
      reloadQueues() // Reload Left panel queues
      setShowPopup(false)
      setQueueToEdit(null)
      toast.success(`Queue ${isEdit ? "updated" : "added"} successfully`)
    } catch (error) {
      console.error(`Failed to ${isEdit ? "update" : "add"} queue`, error)
      toast.error(error)
    } finally {
      setActionLoading(false)
    }
  }

  const handleDeleteQueue = async () => {
    setActionLoading(true)
    try {
      await remove(
        `/admin/queue-fax-number-association/${queueIdToDelete.associationId}`
      )
      fetchQueues()
      reloadQueues() // Reload Left panel queues
      setQueueIdToDelete(null)
      toast.success("Queue removed successfully")
    } catch (error) {
      console.error("Failed to delete queue", error)
      toast.error("Something went wrong")
    } finally {
      setActionLoading(false)
    }
  }

  return (
    <div style={{ display: "flex", justifyContent: "center" }}>
      {loading ? (
        <div className="loading-container-fax-list">
          <div className="loading-message">Loading...</div>
        </div>
      ) : (
        <div
          className="faxes-content"
          style={{ textAlign: "center", marginTop: "20px", width: "100%" }}
        >
          <div
            className="search-container"
            style={{
              width: "100%",
              alignItems: "end",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <div className="setting-note">
              <div>
                <input
                  type="text"
                  placeholder={`Search Queues`}
                  value={searchText}
                  onChange={(e) => {
                    setSearchText(e.target.value)
                    setCurrentPage(1)
                  }}
                />
              </div>
            </div>
            <button
              className="faxes-button"
              onClick={() => setShowPopup(true)}
              style={{ marginBottom: "5px" }}
            >
              + Add Queue
            </button>
          </div>
          <div className="table-container">
            <table className="faxes-table">
              <thead>
                <tr>
                  <th className="faxes-th">Queue Name</th>

                  <th className="faxes-th">Fax Number</th>

                  {/* <th className="faxes-th">Secret</th> */}
                  <th
                    className="faxes-th"
                    style={{ textAlign: "center", width: "100px" }}
                  >
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {currentQueues.length > 0 ? (
                  currentQueues.map((queue) => (
                    <tr
                      key={queue.queueId}
                      className="fax-item"
                      style={{ cursor: "unset" }}
                    >
                      <td className="faxes-td">{queue.queueName}</td>
                      <td className="faxes-td">
                        {formatFaxNumber(queue.faxNumber)}
                      </td>

                      <td className="faxes-td" style={{ textAlign: "center" }}>
                        <span
                          style={{ cursor: "pointer" }}
                          onClick={() => setQueueToEdit(queue)}
                        >
                          <SquarePen size="18" color="black" />
                        </span>
                        <span
                          style={{ cursor: "pointer", marginLeft: "10px" }}
                          onClick={() => setQueueIdToDelete(queue)}
                        >
                          <Trash2 size="18" color="red" />
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="100%"
                      style={{
                        textAlign: "center",
                        padding: "10px",
                        border: "1px solid #ccc",
                      }}
                    >
                      No data found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {filteredQueues.length > 0 && (
            <div
              className="faxes-pagination"
              style={{
                marginTop: "8px",
                display: "flex",
                justifyContent: "end",
              }}
            >
              {filteredQueues.length > 0 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  rowsPerPage={rowsPerPage}
                  totalRows={filteredQueues.length}
                  onPageChange={(page) => setCurrentPage(page)}
                  onRowsPerPageChange={(count) => {
                    setRowsPerPage(count)
                    setCurrentPage(1) // Reset to first page on change
                  }}
                />
              )}
            </div>
          )}
        </div>
      )}

      {(showPopup || queueToEdit) && (
        <QueueForm
          onSave={handleAddQueue}
          onClose={() => {
            setShowPopup(false)
            setQueueToEdit(null)
          }}
          queueToEdit={queueToEdit}
          actionLoading={actionLoading}
        />
      )}

      {queueIdToDelete && (
        <DeleteConfirmationPopup
          onConfirm={handleDeleteQueue}
          onCancel={() => setQueueIdToDelete(null)}
          actionLoading={actionLoading}
          queueIdToDelete={queueIdToDelete}
        />
      )}
    </div>
  )
}

const QueueForm = ({ onClose, onSave, queueToEdit, actionLoading }) => {
  const [queueName, setQueueName] = useState("")
  const [faxNumber, setFaxNumber] = useState("")
  const [errorMessage, setErrorMessage] = useState("")

  // Load initial data if editing
  useEffect(() => {
    if (queueToEdit) {
      setQueueName(queueToEdit?.queueName)
      setFaxNumber(queueToEdit?.faxNumber)
    }
  }, [queueToEdit])

  const handleSubmit = (e) => {
    e.preventDefault()

    if (!queueName.trim()) {
      setErrorMessage("Queue Name is required.")
      return
    }

    if (!faxNumber) {
      setErrorMessage("Fax Number is required")
      return
    } else {
      //const isValidated = validateFaxNumber(faxNumber.trim())
      // if (!isValidated) {
      //   setErrorMessage("Fax Number is incorrect")
      //   return
      // }

      const errorMsg = validateFaxNumberOnlyDigit(faxNumber.trim())

      if (errorMsg) {
        setErrorMessage(errorMsg)
        return
      }
    }

    const params = {
      associationId: queueToEdit?.associationId || null,
      queueName,
      faxNumber: prepareFinalFaxNumber(faxNumber),
    }

    if (
      queueToEdit &&
      ["queueName", "faxNumber"].every(
        (field) => params[field] === queueToEdit[field]
      )
    ) {
      setErrorMessage("Neither Queue Name nor Fax Number has changed")
      return
    }

    onSave(params)
  }

  return (
    <div className="popup-overlay">
      <div className="popup">
        <div className="checkbox-popup-header">
          <h3 className="popup-title">
            {queueToEdit ? `Update` : `Add`} Queue
          </h3>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              fontSize: 24,
              cursor: "pointer",
            }}
          >
            ×
          </button>
        </div>

        <form className="popup-group" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="queueName">
              Queue Name: <span className="mandatory">*</span>
            </label>
            <input
              type="text"
              id="queueName"
              value={queueName}
              onChange={(e) => {
                setQueueName(e.target.value.trim())
                setErrorMessage("")
              }}
            />
          </div>

          <div className="form-group">
            <label htmlFor="netId">
              Fax Number: <span className="mandatory">*</span>
            </label>
            <input
              type="text"
              id="faxNumber"
              value={faxNumber}
              onChange={(e) => {
                setFaxNumber(e.target.value)
                setErrorMessage("")
              }}
            />
          </div>

          {errorMessage && <div className="error-message">{errorMessage}</div>}

          <div className="popup-buttons">
            <button
              className="popup-button cancel"
              type="button"
              onClick={onClose}
            >
              Cancel
            </button>
            <button className="popup-button save" type="submit">
              {actionLoading ? `Saving ...` : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

const DeleteConfirmationPopup = ({
  onConfirm,
  onCancel,
  actionLoading,
  queueIdToDelete,
}) => (
  <div className="popup-overlay">
    <div className="popup" style={{ padding: "20px", maxWidth: "700px" }}>
      {actionLoading ? (
        <div>Processing...</div>
      ) : (
        <>
          {/* <h3>Confirm Deletion</h3> */}
          <p>
            Are you sure you want to delete the queue{" "}
            <strong>{queueIdToDelete?.queueName}?</strong>
          </p>
          {/* <p>
            ⚠️ Deleting this queue will clean up all related associations —
            including fax numbers, admin, and user. This action is irreversible.
          </p> */}
          <div className="popup-buttons">
            <button className="cancel" onClick={onCancel}>
              Cancel
            </button>
            <button className="delete" onClick={onConfirm}>
              Delete
            </button>
          </div>
        </>
      )}
    </div>
  </div>
)

export default QueueToFaxNumberAssociation
